/**
 * The sidebar's Tags section (`070-search-and-links.md` §6, "Sidebar filter").
 *
 * A collapsible list of every tag with its `pageCount`. Clicking one navigates to the tag
 * view. **Multi-select is explicitly not in v1** — one tag at a time.
 *
 * The delete confirm is the part that matters, and its wording is specified, not suggested:
 * *"Remove the tag 'npc' from 12 pages? The pages are not deleted."* Deleting a tag cascades
 * on `page_tag` and nothing else. Say so, out loud, in the dialog — a user who thinks
 * "delete tag" might mean "delete 12 pages" will never use tags again.
 */
import { useState } from 'react';
import { NavLink } from 'react-router';
import { ChevronRight, Pencil, Tag as TagIcon, X } from 'lucide-react';
import type { Tag } from '@/api/endpoints/tags';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from '@/components/ui/sonner';
import { useTranslation } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { useDeleteTag, useTags, useUpdateTag } from '@/features/tags/useTags';

export function TagsSection() {
  const { t, plural } = useTranslation();
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState<Tag | null>(null);
  const [editing, setEditing] = useState<string | null>(null);

  const tags = useTags();
  const { mutate: renameTag } = useUpdateTag();
  const { mutate: deleteTag } = useDeleteTag();

  const rows = [...(tags.data?.tags ?? [])].sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="shrink-0 border-t border-[var(--border)] pt-1">
      <button
        type="button"
        data-testid="tags-toggle"
        aria-expanded={open}
        onClick={() => {
          setOpen((previous) => !previous);
        }}
        className="flex h-[var(--row-h)] w-full items-center gap-1.5 px-[var(--pad-x)] text-[var(--muted-foreground)] hover:bg-[var(--accent)]"
      >
        <ChevronRight className={cn('size-3 transition-transform', open && 'rotate-90')} />
        <TagIcon className="size-[var(--icon)]" strokeWidth={1.75} />
        <span className="text-xs font-medium tracking-wide uppercase">
          {t('tags.section.heading')}
        </span>
      </button>

      {open && (
        <div data-testid="tags-list" className="max-h-48 overflow-y-auto px-1 pb-1">
          {rows.length === 0 ? (
            <p className="px-[var(--pad-x)] py-1 text-[var(--muted-foreground)]">
              {t('tags.section.empty')}
            </p>
          ) : (
            rows.map((tag) =>
              editing === tag.id ? (
                <input
                  key={tag.id}
                  autoFocus
                  defaultValue={tag.name}
                  aria-label={t('tags.renameAria', { name: tag.name })}
                  onBlur={(event) => {
                    const name = event.target.value.trim();
                    setEditing(null);
                    // A rename requires NO page rewrites — assignment is by id (`070` §6).
                    if (name !== '' && name !== tag.name) renameTag({ id: tag.id, name });
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') event.currentTarget.blur();
                    if (event.key === 'Escape') {
                      event.currentTarget.value = tag.name;
                      event.currentTarget.blur();
                    }
                  }}
                  className="h-[var(--row-h)] w-full rounded-sm border border-[var(--ring)] bg-[var(--background)] px-[var(--pad-x)] outline-none"
                />
              ) : (
                <div
                  key={tag.id}
                  data-testid={`tag-row-${tag.name}`}
                  className="group flex h-[var(--row-h)] items-center gap-1 rounded-sm px-[var(--pad-x)] hover:bg-[var(--accent)]"
                >
                  <NavLink
                    to={`/tags/${tag.id}`}
                    className={({ isActive }) =>
                      cn('flex min-w-0 flex-1 items-center gap-1.5', isActive && 'font-medium')
                    }
                  >
                    <TagIcon
                      className="size-[var(--icon)] shrink-0"
                      strokeWidth={1.75}
                      style={tag.color === null ? undefined : { color: tag.color }}
                    />
                    <span className="truncate">{tag.name}</span>
                    <span className="ml-auto shrink-0 pl-1 text-2xs text-[var(--muted-foreground)]">
                      {tag.pageCount}
                    </span>
                  </NavLink>

                  <span className="flex shrink-0 items-center opacity-0 group-hover:opacity-100">
                    <button
                      type="button"
                      aria-label={t('tags.renameAria', { name: tag.name })}
                      title={t('common.rename')}
                      onClick={() => {
                        setEditing(tag.id);
                      }}
                      className="flex size-5 items-center justify-center rounded-sm hover:bg-[var(--border)]"
                    >
                      <Pencil className="size-3" strokeWidth={1.75} />
                    </button>
                    <button
                      type="button"
                      aria-label={t('tags.deleteAria', { name: tag.name })}
                      title={t('tags.deleteTag')}
                      onClick={() => {
                        setConfirm(tag);
                      }}
                      className="flex size-5 items-center justify-center rounded-sm text-[var(--destructive)] hover:bg-[var(--border)]"
                    >
                      <X className="size-3.5" strokeWidth={2} />
                    </button>
                  </span>
                </div>
              ),
            )
          )}
        </div>
      )}

      <AlertDialog
        open={confirm !== null}
        onOpenChange={(next) => {
          if (!next) setConfirm(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm !== null &&
                plural('tags.confirmRemove', confirm.pageCount, { name: confirm.name })}
            </AlertDialogTitle>
            {/* The sentence `070` §6 asks for, verbatim. It is the whole point of the dialog. */}
            <AlertDialogDescription>
              <strong>{t('tags.confirmRemove.description1')}</strong>{' '}
              {t('tags.confirmRemove.description2')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const target = confirm;
                setConfirm(null);
                if (target === null) return;
                deleteTag(target.id);
                toast.success(t('tags.toast.removed', { name: target.name }));
              }}
            >
              {t('tags.removeTagAction')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
